import { useMemo, useEffect, createContext, useContext } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider as MuiLocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// date-fns locales
import { da, de, enGB, enUS } from 'date-fns/locale';

// ----------------------------------------------------------------------

// Simple locale mapping
const LOCALE_MAP: Record<string, any> = {
  da, // Danish
  de, // German
  'en-GB': enGB, // UK English (24-hour)
  'en-US': enUS, // US English (12-hour)
};

// Locale context for date formatting preferences
type LocaleContextType = {
  locale: any;
  use24Hour: boolean;
  getDateTimeFormat: () => string;
  getDateFormat: () => string;
};

const LocaleContext = createContext<LocaleContextType>({
  locale: enGB,
  use24Hour: true,
  getDateTimeFormat: () => 'dd MMM yyyy HH:mm',
  getDateFormat: () => 'dd MMM yyyy',
});

// Hook to access locale preferences
export function useLocale() {
  return useContext(LocaleContext);
}

type Props = {
  children: React.ReactNode;
};

export default function LocalizationProvider({ children }: Props) {
  const localeInfo = useMemo(() => {
    // Detect browser locale
    const browserLocale = navigator.language || 'en-GB';

    // Map to date-fns locale
    let locale = LOCALE_MAP[browserLocale];
    if (!locale) {
      // Try language code only (e.g., 'da' from 'da-DK')
      const langCode = browserLocale.split('-')[0];
      locale = LOCALE_MAP[langCode] || LOCALE_MAP['en-GB'];
    }

    // Determine if 12/24 hour format (only US uses 12-hour by default)
    const use24Hour = !browserLocale.startsWith('en-US');

    // Format helpers
    const getDateTimeFormat = () => (use24Hour ? 'dd MMM yyyy HH:mm' : 'MMM dd, yyyy h:mm a');

    const getDateFormat = () => (use24Hour ? 'dd MMM yyyy' : 'MMM dd, yyyy');

    // Create locale info object
    return {
      locale,
      use24Hour,
      getDateTimeFormat,
      getDateFormat,
    };
  }, []);

  // Set global reference for utility functions (side effect moved to useEffect for React Compiler compliance)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__LOCALE_INFO__ = localeInfo;
    }
  }, [localeInfo]);

  return (
    <LocaleContext.Provider value={localeInfo}>
      <MuiLocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeInfo.locale}>
        {children}
      </MuiLocalizationProvider>
    </LocaleContext.Provider>
  );
}
